import React, { useState } from 'react';
import DataSetColumnFormatModel from '../../Interfaces/Responses/Reports/DataSets/DatasetColumnFormatModel';
import {
    Button,
    Col,
    Divider,
    Input,
    Option,
    Row,
    Select,
    Text,
 } from '@servahealth/serva-ui-kit';
import { StringUtil } from '../../Utils/StringUtil';

type AHRBCoreDataPointCustomizationsFormatProps = {
    dataSetColumnFormat?: DataSetColumnFormatModel;
    dataSetColumnTitle?: DataSetColumnFormatModel;
    updateDataSetColumnFormat: (format: string | undefined) => void;
    updateDataSetColumnTitle: (title: string | undefined) => void;
    close: () => void;
};

const AHRBCoreDataPointCustomizationsFormat: React.FC<AHRBCoreDataPointCustomizationsFormatProps> = ({
    dataSetColumnFormat,
    dataSetColumnTitle,
    updateDataSetColumnFormat,
    updateDataSetColumnTitle,
    close,
}) => {
    const [changeOccurred, setChangedOccurred] = useState(false);
    const [columnFormatValue, setColumnFormatValue] = useState(dataSetColumnFormat?.format ?? '');
    const [columnTitleValue, setColumnTitleValue] =
        useState(dataSetColumnTitle?.columnTitle === '' ? dataSetColumnTitle?.columnName ?? '' : dataSetColumnTitle?.columnTitle ?? '' );

    const handleColumnTitleChange = (aValue: any) => {
        setColumnTitleValue(aValue.target.value);
        setChangedOccurred(true);
    };

    const handleLeftDateChange = (e: string) => {
        setChangedOccurred(true);
        const value = columnFormatValue;
        if (e === '') {
            setColumnFormatValue(value?.replace('MM/dd/yyyy', '').replace('dd/MM/yyyy', '').trim());
        } else {
            if (value?.startsWith('hh') || value === '') {
                setColumnFormatValue(`${e} ${value}`.trim());
            } else {
                setColumnFormatValue(value?.replace('MM/dd/yyyy', e).replace('dd/MM/yyyy', e).trim());
            }
        }
    };

    const handleRightDateChange = (e: string) => {
        setChangedOccurred(true);
        const value = columnFormatValue;
        if (e === '') {
            setColumnFormatValue(value?.replace('hh:mm:ss tt', '').replace('hh:mm:ss', '').replace('hh:mm tt', '').replace('hh:mm', '').trim());
        } else {
            if (!value?.includes('hh') || value === '') {
                setColumnFormatValue(`${value} ${e}`.trim());
            } else {
                let newValue = value?.replace('hh:mm:ss tt', e).replace('hh:mm:ss', e).replace('hh:mm tt', e).trim();
                if (newValue.endsWith('hh:mm') && e !== 'hh:mm') {
                    newValue = newValue.replace('hh:mm', e).trim();
                }
                setColumnFormatValue(newValue);
            }
        }
    };

    const handleLeftNumberChange = (e: string) => {
        setChangedOccurred(true);
        if (columnFormatValue?.length === 0) {
            setColumnFormatValue(e);
        }
        else if (columnFormatValue?.length === 1) {
            if (StringUtil.IsInteger(e)) {
                setColumnFormatValue(`${e}${columnFormatValue}`);
            } else {
                setColumnFormatValue(e);
            }
        }
        else if (columnFormatValue?.length === 2) {
            setColumnFormatValue(`${e}${columnFormatValue[1]}`);
        }
    };

    const handleRightNumberChange = (e: string) => {
        setChangedOccurred(true);
        if (columnFormatValue?.length === 0) {
            setColumnFormatValue(e);
        }
        else if (columnFormatValue?.length === 1) {
            if (StringUtil.IsInteger(e)) {
                setColumnFormatValue(e);
            } else {
                setColumnFormatValue(`${columnFormatValue}${e}`);
            }
        }
        else if (columnFormatValue?.length === 2) {
            setColumnFormatValue(`${columnFormatValue[0]}${e}`);
        }
    };

    const leftDropdown = () => {
        if (dataSetColumnFormat?.jsDataType === 'date') {
            let value = columnFormatValue;
            if (value?.startsWith('MM/dd/yyyy')) {
                value = 'MM/dd/yyyy';
            } else if (value?.startsWith('dd/MM/yyyy')) {
                value = 'dd/MM/yyyy';
            } else {
                value = '';
            }
            return (
                <Select
                    label="Date Part Format:"
                    style={{ width: '90%' }}
                    value={value}
                    onChange={(e: string) => handleLeftDateChange(e)}
                >
                    <Option value="">None</Option>
                    <Option value="MM/dd/yyyy">Month/Day/Year</Option>
                    <Option value="dd/MM/yyyy">Day/Month/Year</Option>
                </Select>
            );
        }
        else if (dataSetColumnFormat?.jsDataType === 'number') {
            let value = columnFormatValue;
            if (value?.startsWith('N')) {
                value = 'N';
            } else if (value?.startsWith('F')) {
                value = 'F';
            } else if (value?.startsWith('P')) {
                value = 'P';
            } else {
                value = '';
            }
            return (
                <Select
                    label="Number Type:"
                    style={{ width: '90%' }}
                    value={value}
                    onChange={(e: string) => handleLeftNumberChange(e)}
                >
                    <Option value="">None</Option>
                    <Option value="N">Integer</Option>
                    <Option value="F">Decimal</Option>
                    <Option value="P">Percentage</Option>
                </Select>
            );
        }
    };

    const rightDropdown = () => {
        if (dataSetColumnFormat?.jsDataType === 'date') {
            let value = columnFormatValue;
            if (value?.endsWith('hh:mm')) {
                value = 'hh:mm';
            } else if (value?.endsWith('hh:mm tt')) {
                value = 'hh:mm tt';
            } else if (value?.endsWith('hh:mm:ss')) {
                value = 'hh:mm:ss';
            } else if (value?.endsWith('hh:mm:ss tt')) {
                value = 'hh:mm:ss tt';
            } else {
                value = '';
            }
            return (
                <Select
                    label="Time Part Format:"
                    style={{ width: '90%' }}
                    value={value}
                    onChange={(e: string) => handleRightDateChange(e)}
                >
                    <Option value="">None</Option>
                    <Option value="hh:mm">Hour:Minute</Option>
                    <Option value="hh:mm tt">Hour:Minute AM/PM</Option>
                    <Option value="hh:mm:ss">Hour:Minute:Second</Option>
                    <Option value="hh:mm:ss tt">
                        Hour:Minute:Second AM/PM
                    </Option>
                </Select>
            );
        }
        else if (dataSetColumnFormat?.jsDataType === 'number') {
            let value = columnFormatValue;
            if (value?.endsWith('0')) {
                value = '0';
            } else if (value?.endsWith('1')) {
                value = '1';
            } else if (value?.endsWith('2')) {
                value = '2';
            } else if (value?.endsWith('3')) {
                value = '3';
            } else {
                value = '';
            }
            return (
                <Select
                    label="Decimal Places"
                    style={{ width: '90%' }}
                    value={value}
                    onChange={(e: string) => handleRightNumberChange(e)}
                >
                    <Option value="">As Needed</Option>
                    <Option value="0">{'0 (i.e "5")'}</Option>
                    <Option value="1">{'1 (i.e "5.3")'}</Option>
                    <Option value="2">{'2 (i.e "5.25")'}</Option>
                    <Option value="3">{'3 (i.e "5.253")'}</Option>
                </Select>
            );
        }
    };

    const handleApply = () => {
        if (dataSetColumnFormat !== undefined) {
            updateDataSetColumnFormat(columnFormatValue);
        }
        else {
            updateDataSetColumnTitle(columnTitleValue);
        }

        close();
    };

    const contentStyle: React.CSSProperties = {
        lineHeight: '95%',
        textAlign: 'center',
        color: 'black',
        backgroundColor: 'azure',
        borderRadius: '10',
        border: `1px solid black`,
        marginTop: 4,
        height: '95%'
    };

    return (
        <>
            <Row gutter={16} className='retainbi-compact-row'>
                <Col span={4} />
                <Col span={16}>
                    <div style={contentStyle}>
                        <h3 className='text-2xl pt-2 px-2'>Formatting: {(dataSetColumnFormat !== undefined) ? 'Column Format' : 'Column Title'}</h3>
                    </div>
                </Col>
                <Col span={4} />
            </Row>
            <Divider></Divider>
            {
                (
                    dataSetColumnFormat !== undefined
                )
                ?
                (
                    <Row gutter={16} className='retainbi-compact-row'>
                        <Col span={11}>{leftDropdown()}</Col>
                        <Col span={2}></Col>
                        <Col span={11}>{rightDropdown()}</Col>
                    </Row>
                )
                :
                (
                    <Row gutter={16} className='retainbi-compact-row'>
                        <Col span={6}>
                            <div style={{padding:'4px', textAlign:'left'}}>
                                <Text>Column Title:</Text>
                            </div>
                        </Col>
                        <Divider style={{ border: '1px solid lightgray', margin: '4px' }} />
                        <Col span={12}>
                            <div style={{ textAlign: 'left' }}>
                                <Input
                                    allowClear={true}
                                    value={columnTitleValue}
                                    onChange={handleColumnTitleChange}
                                ></Input>
                            </div>
                        </Col>
                    </Row>
                )
            }
            <Divider></Divider>
            <Row gutter={16} className='retainbi-compact-row'>
                <Col span={15} />
                <Col span={4}>
                    <div style={{ textAlign: 'right' }}>
                        <Button
                            disabled={!changeOccurred}
                            htmlType="button"
                            style={{width: '95%'}}
                            type='primary'
                            onClick={handleApply}
                        >
                            Apply
                        </Button>
                    </div>
                </Col>
                <Col span={4}>
                    <div style={{ textAlign: 'right' }}>
                        <Button
                            style={{width: '95%'}}
                            type="default" onClick={() => close()}>
                            Cancel
                        </Button>
                    </div>
                </Col>
                <Col span={1} />
            </Row>
        </>
    );
};

export default AHRBCoreDataPointCustomizationsFormat;
